import React, { useCallback, useEffect, useRef, useState } from "react";
import { Dropdown, IDropdownOption, Icon, ResponsiveMode, Spinner, SpinnerSize, TooltipHost } from "@fluentui/react";
import { DefaultButton, PrimaryButton } from "@fluentui/react/lib/Button";
import { TreeViewCheckChangeEvent, TreeViewExpandChangeEvent } from "@progress/kendo-react-treeview";
import * as uuid from "uuid";
import { Template } from "../interfaces/Template";
import { getTemplates } from "../../api/document/Templates";
import {
  fetchDocumentMetadata,
  fetchRootAndMultilinkedDescendants,
  triggerChangeTemplateElaboration,
} from "../../api/document/DocumentsPart";
import { DocumentPart } from "../../api/models/document-part";
import { formatMessage, formatMessageDefault } from "../../utils/helpers/LanguageHelper";
import { ButtonVariants } from "../../styles/fluent/ButtonVariants";
import { FlatPathTreeItem } from "../generic/FlatPathTreeExplorer";
import { CustomTreeView } from "../cpm/CustomTreeView";
import { TreeViewDataItem } from "../cpm/DocumentPartTree";
import FooterPanel from "../layout/FooterPanel";
import HeaderPanel from "../layout/HeaderPanel";
import {
  CAPTION_CHANGE_TEMPLATE_CONFIRM_BODY,
  CAPTION_CHANGE_TEMPLATE_INFO_DOC_SELECTION,
  CAPTION_CHANGE_TEMPLATE_SELECT_DOCUMENTS,
  CAPTION_CHANGE_TEMPLATE_TEMPLATE_LABEL,
  CAPTION_CHANGE_TEMPLATE_TITLE,
} from "./outlineCaptions";
import { useAppSelector } from "../../redux/hooks/hooks";
import {
  elaborationStatusItemIsRunning,
  selectElaborationStatusList,
} from "../../redux/feature/object/remote-elaborations-slice";

const CAPTION_LOADING_DATA = "cof.base.loadingdata";
const CAPTION_BACK = "cof.base.back";
const CAPTION_NEXT = "cof.base.next";
const CAPTION_NO_OPTIONS_AVAILABLE = "cof.base.nooptionsavailable";
const CAPTION_SELECT = "cof.base.select";
const CAPTION_APPLY = "cof.base.confirm";
const DEFAULT_TEMPLATE_KEY = "default";

export interface ChangeTemplateProps {
  selectedTreeItem: FlatPathTreeItem;
  rootDocumentId: string;
  channelIdToNotify?: string;
  onBackButtonClick: () => void;
}

type ChangeTemplatePage = "loadingParts" | "selectParts" | "selectTemplate";

const mapMultilinkedPartsToTreeItems = (parts: DocumentPart[]): TreeViewDataItem[] =>
  parts.reduce<TreeViewDataItem[]>((treeItems, part) => {
    if (part.id) {
      treeItems.push({
        text: part.name ?? part.id,
        key: part.id,
        hasChildren: false,
        nodeType: "part",
        checked: false,
        data: { ...part, multiLinked: true },
        hasReadPermissions: true,
      });
    }

    return treeItems;
  }, []);

const mapDocumentPartsToSelectionTree = (
  rootPart: DocumentPart,
  multilinkedParts: DocumentPart[],
  rootDocumentId: string,
  rootName: string
): TreeViewDataItem[] => {
  return [
    {
      text: rootName,
      key: rootDocumentId,
      hasChildren: false,
      nodeType: "root",
      checked: false,
      data: { ...rootPart, id: rootDocumentId, name: rootName, multiLinked: false },
      hasReadPermissions: true,
    },
    ...mapMultilinkedPartsToTreeItems(multilinkedParts),
  ];
};

const updateTreeItemAtPath = (
  treeItems: TreeViewDataItem[],
  indexes: number[],
  updateItem: (item: TreeViewDataItem) => TreeViewDataItem
): TreeViewDataItem[] =>
  treeItems.map((item, index) => {
    if (index !== indexes[0]) {
      return item;
    }

    if (indexes.length === 1) {
      return updateItem(item);
    }

    return {
      ...item,
      items: updateTreeItemAtPath(item.items ?? [], indexes.slice(1), updateItem),
    };
  });

const getTreeItemIndexes = (itemHierarchicalIndex: string): number[] =>
  itemHierarchicalIndex.split("_").map((index) => Number(index));

export const ChangeTemplate: React.FunctionComponent<ChangeTemplateProps> = (props) => {
  const documentId = props.rootDocumentId;
  const [page, setPage] = useState<ChangeTemplatePage>("loadingParts");
  const [hasMultilinked, setHasMultilinked] = useState(false);
  const [rootName, setRootName] = useState(props.selectedTreeItem.text);
  const [partsTreeItems, setPartsTreeItems] = useState<TreeViewDataItem[]>([]);
  const [checkedParts, setCheckedParts] = useState<Map<string, string>>(new Map());
  const [isLoading, setIsLoading] = useState(true);
  const [templateOptions, setTemplateOptions] = useState<IDropdownOption[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [pendingElaborationId, setPendingElaborationId] = useState<string>();
  const elaborations = useAppSelector(selectElaborationStatusList);
  const partsRequestIdRef = useRef(0);
  const templateRequestIdRef = useRef(0);

  const loadPartsData = useCallback(async (): Promise<void> => {
    const requestId = ++partsRequestIdRef.current;
    setPage("loadingParts");
    setHasMultilinked(false);
    setPartsTreeItems([]);
    setCheckedParts(new Map());

    try {
      const documentParts = await fetchRootAndMultilinkedDescendants(documentId);
      if (requestId !== partsRequestIdRef.current) {
        return;
      }

      if (!documentParts?.length) {
        setPage("selectTemplate");
        return;
      }

      const matchingRootIndex = documentParts.findIndex((part) => part.id === documentId);
      const rootPartIndex = matchingRootIndex >= 0 ? matchingRootIndex : 0;
      const rootPart = documentParts[rootPartIndex];
      const multilinkedParts = documentParts.filter((_part, index) => index !== rootPartIndex);
      const fetchedRootName = rootPart.name ?? props.selectedTreeItem.text;
      const shouldSelectParts = multilinkedParts.length > 0;

      setRootName(fetchedRootName);
      setHasMultilinked(shouldSelectParts);
      setPartsTreeItems(
        shouldSelectParts
          ? mapDocumentPartsToSelectionTree(rootPart, multilinkedParts, documentId, fetchedRootName)
          : []
      );
      setPage(shouldSelectParts ? "selectParts" : "selectTemplate");
    } catch (error) {
      console.error("Failed to load the root and multilinked document parts", { documentId, error });
      if (requestId === partsRequestIdRef.current) {
        // Preserve the original flow if the flat multilinked data source is unavailable.
        setPage("selectTemplate");
      }
    }
  }, [documentId, props.selectedTreeItem.text]);

  const loadTemplateData = useCallback(async (): Promise<void> => {
    const requestId = ++templateRequestIdRef.current;
    setIsLoading(true);

    // Each fetch is caught individually so one failing doesn't prevent the other from still populating the
    // picker; the axios interceptor already surfaces a generic error notification for either failure on its own.
    const [fetchedTemplates, metadata] = await Promise.all([
      getTemplates().catch((error) => {
        console.error("Failed to load templates", { documentId, error });
        return [] as Template[];
      }),
      fetchDocumentMetadata(documentId, false).catch((error) => {
        console.error("Failed to load the document's current template", { documentId, error });
        return undefined;
      }),
    ]);

    if (requestId !== templateRequestIdRef.current) {
      return;
    }

    const options: IDropdownOption[] = fetchedTemplates
      .filter((template) => template.templateId !== DEFAULT_TEMPLATE_KEY)
      .map((template) => ({
        key: template.templateId,
        text: template.templateName,
      }));

    const currentTemplateInfo = metadata?.templateInfo;
    const currentTemplateId = currentTemplateInfo?.templateId;
    const canPreselectCurrentTemplate =
      !!currentTemplateId &&
      currentTemplateId !== DEFAULT_TEMPLATE_KEY &&
      options.some((option) => option.key === currentTemplateId);

    setTemplateOptions(options);
    setSelectedTemplateId(canPreselectCurrentTemplate ? currentTemplateId : undefined);
    setIsLoading(false);
  }, [documentId]);

  useEffect(() => {
    void loadPartsData();
    void loadTemplateData();

    return () => {
      partsRequestIdRef.current++;
      templateRequestIdRef.current++;
    };
  }, [loadPartsData, loadTemplateData]);

  useEffect(() => {
    if (!pendingElaborationId) {
      return;
    }

    const elaboration = elaborations?.find((item) => item.correlationId === pendingElaborationId);
    const changeTemplateItem = elaboration?.items.find((item) => item.elaborationType === "CHANGE_TEMPLATE");
    if (changeTemplateItem && !elaborationStatusItemIsRunning(changeTemplateItem)) {
      setPendingElaborationId(undefined);
      props.onBackButtonClick();
    }
  }, [elaborations, pendingElaborationId]);

  const handlePartsCheckChange = (event: TreeViewCheckChangeEvent) => {
    const itemId = event.item.key;
    if (typeof itemId !== "string" || !event.itemHierarchicalIndex) {
      return;
    }

    const indexes = getTreeItemIndexes(event.itemHierarchicalIndex);
    if (indexes.some((index) => Number.isNaN(index))) {
      return;
    }

    const isChecked = !event.item.checked;
    setPartsTreeItems((items) =>
      updateTreeItemAtPath(items, indexes, (item) => ({
        ...item,
        checked: isChecked,
      }))
    );
    setCheckedParts((checked) => {
      const updatedCheck = new Map(checked);
      if (isChecked) {
        updatedCheck.set(itemId, typeof event.item.text === "string" ? event.item.text : itemId);
      } else {
        updatedCheck.delete(itemId);
      }
      return updatedCheck;
    });
  };

  const handlePartsExpandChange = (event: TreeViewExpandChangeEvent) => {
    if (!event.itemHierarchicalIndex) {
      return;
    }

    const indexes = getTreeItemIndexes(event.itemHierarchicalIndex);
    if (indexes.some((index) => Number.isNaN(index))) {
      return;
    }

    const isExpanded = !event.item.expanded;
    setPartsTreeItems((items) =>
      updateTreeItemAtPath(items, indexes, (item) => ({
        ...item,
        expanded: isExpanded,
      }))
    );
  };

  const showTemplatePage = () => {
    if (checkedParts.size === 0) {
      return;
    }
    setPage("selectTemplate");
  };

  const confirmChangeTemplate = async () => {
    if (!selectedTemplateId) {
      return;
    }

    setIsSubmitting(true);
    const correlationId = uuid.v4();
    try {
      const selectedRootId = !hasMultilinked || checkedParts.has(documentId) ? documentId : undefined;
      const multilinkedPartIds = hasMultilinked
        ? Array.from(checkedParts.keys()).filter((partId) => partId !== documentId)
        : [];

      setPendingElaborationId(correlationId);

      await triggerChangeTemplateElaboration(
        documentId,
        selectedTemplateId,
        props.channelIdToNotify,
        selectedRootId,
        multilinkedPartIds,
        correlationId
      );
    } catch (error) {
      console.error("Failed to trigger the change-template elaboration", {
        documentId,
        templateId: selectedTemplateId,
        error,
      });
      setPendingElaborationId(undefined);
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderLoading = () => (
    <output
      data-testid="change-template-loading"
      style={{
        alignItems: "center",
        display: "flex",
        justifyContent: "center",
        minHeight: "70vh",
        width: "100%",
      }}
    >
      <Spinner label={formatMessage(CAPTION_LOADING_DATA)} size={SpinnerSize.medium} />
    </output>
  );

  const renderTemplateContent = () => {
    if (isLoading) {
      return renderLoading();
    }

    return (
      <div style={{ padding: "0 0.5rem" }} data-testid="change-template-ready">
        <Dropdown
          data-testid="change-template-dropdown"
          label={formatMessage(CAPTION_CHANGE_TEMPLATE_TEMPLATE_LABEL)}
          placeholder={formatMessage(templateOptions.length === 0 ? CAPTION_NO_OPTIONS_AVAILABLE : CAPTION_SELECT)}
          options={templateOptions}
          selectedKey={selectedTemplateId}
          responsiveMode={ResponsiveMode.large}
          onChange={(_event, option) => setSelectedTemplateId(option?.key.toString())}
        />
        <div data-testid="change-template-warning" style={{ marginTop: "0.75rem" }}>
          {formatMessage(CAPTION_CHANGE_TEMPLATE_CONFIRM_BODY)}
        </div>
      </div>
    );
  };

  const renderPartsContent = () => (
    <div
      data-testid="change-template-parts"
      style={{
        flex: 1,
        minHeight: 0,
        overflow: "auto",
        padding: "12px 8px 0",
      }}
    >
      <div
        style={{
          alignItems: "center",
          display: "inline-flex",
          marginBottom: "8px",
          whiteSpace: "nowrap",
        }}
      >
        <span>{formatMessage(CAPTION_CHANGE_TEMPLATE_SELECT_DOCUMENTS)}</span>
        <TooltipHost
          content={formatMessageDefault(
            CAPTION_CHANGE_TEMPLATE_INFO_DOC_SELECTION,
            "Select the documents to include in the template change."
          )}
        >
          <span
            aria-label={formatMessageDefault(
              CAPTION_CHANGE_TEMPLATE_INFO_DOC_SELECTION,
              "When selecting a root part, the system applies the chosen template to all its child parts, except for multi-linked parts. To update multi-linked parts, select them separately from the list. The new template will then be applied to the selected multi-linked parts and their child parts."
            )}
            data-testid="change-template-parts-info-icon"
            style={{ display: "inline-flex", marginLeft: "4px", cursor: "default" }}
          >
            <Icon iconName="InfoSolid" style={{ color: "var(--color-blue)", fontSize: "14px" }} />
          </span>
        </TooltipHost>
      </div>
      <CustomTreeView
        data={partsTreeItems}
        expandIcons={true}
        onExpandChange={handlePartsExpandChange}
        size="small"
        checkboxes={true}
        onCheckChange={handlePartsCheckChange}
        selectedItemId=""
        treeItemRowProps={{
          certificationOptions: () => ({ items: [] }),
        }}
      />
    </div>
  );

  const handleBackButtonClick = () => {
    if (page === "selectTemplate" && hasMultilinked) {
      setPage("selectParts");
      return;
    }
    props.onBackButtonClick();
  };

  return (
    <div
      style={{
        display: "flex",
        flex: 1,
        flexDirection: "column",
        height: "100%",
        marginBottom: "-2.5rem",
        minHeight: 0,
        overflow: "hidden",
      }}
    >
      <div style={{ flexShrink: 0 }}>
        <HeaderPanel data-testid="header" applyPadding>
          {page === "selectParts"
            ? formatMessage(CAPTION_CHANGE_TEMPLATE_TITLE) + " - " + rootName
            : formatMessage(CAPTION_CHANGE_TEMPLATE_TITLE)}
        </HeaderPanel>
      </div>
      <div
        style={{
          boxSizing: "border-box",
          display: "flex",
          flex: 1,
          flexDirection: "column",
          minHeight: 0,
          overflow: "auto",
          paddingBottom: "110px",
        }}
      >
        {page === "loadingParts"
          ? renderLoading()
          : page === "selectParts"
            ? renderPartsContent()
            : renderTemplateContent()}
      </div>
      <FooterPanel>
        <DefaultButton
          data-testid="change-template-back"
          text={formatMessage(CAPTION_BACK)}
          onClick={handleBackButtonClick}
          style={{ float: "left" }}
        />
        {page === "selectParts" ? (
          <PrimaryButton
            className={ButtonVariants.primary}
            data-testid="change-template-next"
            text={formatMessage(CAPTION_NEXT)}
            disabled={checkedParts.size === 0}
            onClick={showTemplatePage}
            style={{ float: "right" }}
          />
        ) : (
          <PrimaryButton
            className={ButtonVariants.primary}
            data-testid="change-template-apply"
            text={formatMessage(CAPTION_APPLY).toUpperCase()}
            disabled={
              page !== "selectTemplate" ||
              isLoading ||
              !selectedTemplateId ||
              isSubmitting ||
              pendingElaborationId !== undefined
            }
            onClick={() => void confirmChangeTemplate()}
            style={{ float: "right" }}
          />
        )}
      </FooterPanel>
    </div>
  );
};
